import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { MeResponse, type AdoptResponse, type AlreadySignedIn } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { rekeyLocalData } from "../../lib/rekey-local-data.ts";
import { useSession } from "../../lib/session.tsx";
import {
  type AdoptRefusal,
  adoptRefusalOf,
  cannotText,
  consequenceText,
  deviceStore,
  elsewhereRefusalText,
  IN_LESSON_LINK_TEXT,
  inLessonTitle,
  offerTexts,
  rememberNo,
  remembersAnswer,
  scopeSummary,
} from "./adopt-texts.ts";

/**
 * Вопрос «это один ученик?» вместо тупика «Вы уже вошли» (раздел 9
 * `docs/account-adopt-design.md`).
 *
 * Рисуется в `JoinPage` на 409 погашения ПОСТОЯННОЙ ссылки, когда сервер
 * прислал `adopt`. Без `adopt` (и при запомненном «Нет», 9.4) остаётся старый
 * экран. Ссылка на занятие сюда не ведёт вовсе (С8): `lesson-link-gate.tsx`
 * этим проектом не меняется.
 *
 * Читатель это ребёнок, родитель с общего телефона или сам преподаватель,
 * открывший ссылку проверить. Тексты собраны в `adopt-texts.ts`, где они под
 * тестами; здесь только разметка и переходы.
 *
 * ⚠️ Любая кнопка, которая закрывает вход в этом браузере (`signOut`), несёт
 * под собой строку последствия: чей вход закроется и как вернуться (9, М6).
 * Без неё «Нет» на общем ноутбуке выбивало бы сестру, не сказав, где её
 * ссылка.
 */
export function AdoptConfirm(props: {
  code: string;
  info: AlreadySignedIn & { adopt: NonNullable<AlreadySignedIn["adopt"]> };
  /** Выйти и войти по этой ссылке: «Нет» и все «Войти по этой ссылке». */
  onLeave: () => Promise<void>;
  /** То же, а потом в урок, который идёт (экран «на занятии»). */
  onLeaveToLesson: () => Promise<void>;
  /** Погасить ссылку ещё раз, не выходя: `adopt_stale` и `same_user`. */
  onRetry: (opts: { grew: boolean }) => void;
  /** Отказ, которого этот экран не рисует: общий «Ссылка не сработала». */
  onError: (e: Error) => void;
}) {
  const navigate = useNavigate();
  const { adopt, signOut } = useSession();
  const { info } = props;
  const cur = info.adopt.currentDisplayName;
  const stu = info.studentDisplayName;
  const tch = info.teacherDisplayName;
  // Список преподавателей вошедшего приходит только с предложением (8.1)
  const currentTeachers = info.adopt.verdict === "offer" ? info.adopt.currentTeachers : null;
  const elsewhere = info.adopt.verdict === "cannot" && info.adopt.openElsewhere === true;

  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdoptRefusal | null>(null);
  const [done, setDone] = useState<AdoptResponse | null>(null);

  const remember = (): void => {
    if (info.linkStudentUserId !== undefined) {
      rememberNo(deviceStore(), info.currentUserId, info.linkStudentUserId);
    }
  };

  // Показанный экран «нельзя» запоминается сразу (9.4): причина стойкая, и
  // второй раз этот вопрос на этом устройстве ни к чему
  const cannot = info.adopt.verdict === "cannot" && !elsewhere;
  const { currentUserId, linkStudentUserId } = info;
  useEffect(() => {
    if (!cannot || !remembersAnswer("cannot") || linkStudentUserId === undefined) return;
    rememberNo(deviceStore(), currentUserId, linkStudentUserId);
  }, [cannot, currentUserId, linkStudentUserId]);

  const back = (): void => void navigate("/", { replace: true });

  /**
   * Выход и вход по ссылке. Отказ `signOut` не теряется необработанным: он
   * уводит на общий экран «Ссылка не сработала» с «Повторить», а вход в этом
   * браузере к этому моменту уже погашен (состояние сессии `anon`), поэтому
   * повтор гасит ссылку как обычно.
   */
  const leave = (then: () => Promise<void>): void => {
    setBusy(true);
    void then()
      .catch((e: unknown) => props.onError(e instanceof Error ? e : new Error(String(e))))
      .finally(() => setBusy(false));
  };

  const confirm = (): void => {
    setBusy(true);
    void (async () => {
      try {
        const res = await api.confirmSameStudent(props.code, info.currentUserId);
        // ⚠️ Заглушкой стал владелец ЭТОЙ сессии: ключи устройства под его
        // старым id переписываются ДО `adopt`, иначе первый же эффект сессии
        // заведёт очереди под новым id и не увидит неотправленного (9.7).
        // Проверка стоит ВЫШЕ ветки `same_user`: сессию этого браузера мог
        // перевесить встречный перенос с другого устройства, и тогда ответ
        // `same_user` приходит уже с новым id
        if (res.userId !== info.currentUserId) await rekeyLocalData(info.currentUserId, res.userId);
        if (res.result === "same_user") {
          // Ссылка уже ведёт к вошедшему: перенос сделан с другого устройства.
          // Дальше как погашение, с итогом на приветствии (9.2, 9.5)
          props.onRetry({ grew: true });
          return;
        }
        adopt(MeResponse.parse(res));
        setDone(res);
      } catch (e) {
        const r = e instanceof api.ApiError ? adoptRefusalOf(e.code, e.details) : null;
        if (r === null) {
          props.onError(e instanceof Error ? e : new Error(String(e)));
          return;
        }
        if (remembersAnswer(r.kind)) remember();
        setRefusal(r);
      } finally {
        setBusy(false);
      }
    })();
  };

  const consequence = (teachers: readonly string[] | null) => (
    <p className="muted">{consequenceText(cur, teachers)}</p>
  );

  const signInHere = (label: string, teachers: readonly string[] | null, extra?: string) => (
    <div className="adopt__choice">
      <button type="button" className="btn" disabled={busy} onClick={() => leave(props.onLeave)}>
        {label}
      </button>
      <p className="muted">
        {consequenceText(cur, teachers)}
        {extra !== undefined && ` ${extra}`}
      </p>
    </div>
  );

  /**
   * «Войти по паролю» на экранах строки 9б (ревью кода 6, круг 2, М2): у
   * ученика с паролем путь через ссылки не сработает никогда, а войти паролем,
   * не выйдя, нельзя. Кнопка выходит и ведёт на экран входа; ссылка остаётся
   * в истории браузера (без `replace`), к ней можно вернуться кнопкой «назад».
   */
  const passwordButton = () => (
    <div className="adopt__choice">
      <button
        type="button"
        className="btn btn--quiet"
        disabled={busy}
        onClick={() =>
          leave(async () => {
            await signOut();
            void navigate("/");
          })
        }
      >
        Войти по паролю
      </button>
      <p className="muted">
        {consequenceText(cur, currentTeachers)} После входа откройте эту ссылку ещё раз.
      </p>
    </div>
  );

  const backButton = (primary = false) => (
    <button
      type="button"
      className={primary ? "btn btn--primary" : "btn btn--quiet"}
      disabled={busy}
      onClick={back}
    >
      Вернуться в кабинет
    </button>
  );

  if (done !== null) {
    const summary = scopeSummary(done.scopeTeachers);
    return (
      <main className="lobby">
        <h1>Готово</h1>
        {summary !== null && <p>{summary}</p>}
        <p className="muted">
          На другом телефоне или компьютере откройте ссылки от каждого преподавателя, тогда занятия
          будут видны и там. Если ссылки нет, попросите её у преподавателя. Сохраните все ссылки: через
          несколько месяцев этот браузер попросит войти снова, и занятия откроются по ним.
        </p>
        <p className="muted">Если это была ошибка, скажите об этом преподавателю «{done.teacherDisplayName}».</p>
        {/* Путь дальше один: вес это говорит прямо, как на приветствии */}
        <button type="button" className="btn btn--primary" onClick={back}>
          Продолжить
        </button>
      </main>
    );
  }

  if (refusal !== null) {
    switch (refusal.kind) {
      case "stale":
        return (
          <main className="lobby">
            {/* Одна строка на все причины: сменился вход, откат, новая пара
                у преподавателя (9.3, ревью 3 продукта, Б2) */}
            <h1>Пока открывалась ссылка, что-то изменилось</h1>
            <button type="button" className="btn" onClick={() => props.onRetry({ grew: false })}>
              Открыть ссылку ещё раз
            </button>
          </main>
        );
      case "needs_support":
        return (
          <main className="lobby">
            <h1>Соединить занятия не получилось</h1>
            <p>Скажите об этом преподавателю «{tch}». Пока можно войти по этой ссылке отдельно.</p>
            {signInHere("Войти по этой ссылке", currentTeachers)}
            {backButton()}
          </main>
        );
      case "elsewhere":
        return (
          <main className="lobby">
            <h1>Соединить занятия здесь пока не получится</h1>
            <p>{elsewhereRefusalText(cur)}</p>
            {signInHere("Войти по этой ссылке", currentTeachers)}
            {passwordButton()}
            {backButton()}
          </main>
        );
      case "in_lesson":
        if (!refusal.stubIsCurrent) {
          // Заглушка это ученик ссылки: он в комнате у преподавателя ссылки,
          // и путь туда не закрывается (3.3: отказ не тупик)
          return (
            <main className="lobby">
              <h1>{inLessonTitle(stu)}</h1>
              <p>{IN_LESSON_LINK_TEXT}</p>
              <div className="adopt__choice">
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => leave(props.onLeaveToLesson)}
                >
                  Войти на занятие
                </button>
                {consequence(currentTeachers)}
              </div>
              {backButton()}
            </main>
          );
        }
        return (
          <main className="lobby">
            <h1>Соединить занятия сейчас не получится</h1>
            <p>
              {inLessonTitle(cur)}. Соединить занятия получится после урока: откройте эту ссылку ещё
              раз.
            </p>
            {backButton(true)}
            {/*
              ⚠️ Тупика быть не должно (ревью 3 продукта, Б1): предикат «на
              занятии» не отличает соседнюю вкладку от другого устройства и от
              брошенного урока. Поэтому вход по ссылке остаётся, но тихо и с
              ещё одной фразой о цене
            */}
            <div className="adopt__choice">
              <button
                type="button"
                className="btn btn--quiet"
                disabled={busy}
                onClick={() => leave(props.onLeave)}
              >
                Войти по этой ссылке
              </button>
              <p className="muted">
                {consequenceText(cur, currentTeachers)} Если занятие открыто в соседней вкладке, оно
                там прервётся.
              </p>
            </div>
          </main>
        );
    }
  }

  if (info.adopt.verdict === "cannot") {
    return (
      <main className="lobby">
        <h1>Ссылка для ученика «{stu}»</h1>
        <p>
          {cannotText({
            currentDisplayName: cur,
            studentDisplayName: stu,
            teacherDisplayName: tch,
            elsewhere,
          })}
        </p>
        {signInHere("Войти по этой ссылке", null)}
        {elsewhere && passwordButton()}
        {backButton()}
      </main>
    );
  }

  const t = offerTexts({
    currentDisplayName: cur,
    studentDisplayName: stu,
    teacherDisplayName: tch,
    currentTeachers: info.adopt.currentTeachers,
  });
  return (
    <main className="lobby">
      <h1>{t.title}</h1>
      {t.names !== null && <p className="muted">{t.names}</p>}
      <p>{t.now}</p>
      <p>{t.link}</p>
      <p className="muted">{t.outcome}</p>
      {/*
        ⚠️ Обе кнопки обычного веса. Законных исходов два, а общий ноутбук
        семьи (С6) делает заметную «Да» опасной: ошибочное «Да» соединяет
        занятия двух детей, и разводит их потом только владелец вручную
      */}
      <button type="button" className="btn" disabled={busy} onClick={confirm}>
        Да, это один ученик
      </button>
      <div className="adopt__choice">
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => {
            remember();
            leave(props.onLeave);
          }}
        >
          Нет, это разные люди
        </button>
        {consequence(currentTeachers)}
      </div>
      {backButton()}
    </main>
  );
}
